import { FC } from "react";
import { IOrder } from "../../../model/IOrder";
import priceFormatter from "../../../utils/priceFormatter";
import {
  feedPrice,
  freightDiscount,
  partPrice,
  subtotal,
  vendorPrice,
} from "../../../utils/orderTotals";
import { DetailPanel, DetailRow, DetailTable } from "./DetailPanel";

const money = (value: number | null | undefined) =>
  value === null || value === undefined ? null : priceFormatter(value);

const stamp = (value: string | null) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value
    : `${date.toLocaleDateString('en-CA')} ${date.toLocaleTimeString('en-GB')}`;
};

const RefundTable: FC<{ order: IOrder, vendor: boolean }> = ({ order, vendor }) => {
  const rows = (order.refundItems || []).filter((r) => !!r.isVendorRefund === vendor);

  return (
    <DetailTable
      head={['Type', 'Part', 'Freight', 'Shipping Protection', 'Tax', 'Vendor Code']}
      empty={vendor ? 'No vendor refunds' : 'No refunds or chargebacks'}
    >
      {rows.map((refund, i) => [
        <tr key={`r${refund.id ?? i}`} className="odd:bg-gray-50">
          <td className="py-2 px-2 whitespace-nowrap font-medium">{refund.type || '—'}</td>
          <td className="py-2 px-2 text-right">{money(refund.partPrice) ?? '—'}</td>
          <td className="py-2 px-2 text-right">{money(refund.freight) ?? '—'}</td>
          <td className="py-2 px-2 text-right">{money(refund.automotiveCare) ?? '—'}</td>
          <td className="py-2 px-2 text-right">{money(refund.taxes) ?? '—'}</td>
          <td className="py-2 px-2 whitespace-nowrap">{refund.supplierKey || '—'}</td>
        </tr>,
        // The old page gave comments their own full width row under each record.
        refund.comments
          ? (
            <tr key={`c${refund.id ?? i}`} className="odd:bg-gray-50">
              <td colSpan={6} className="pb-3 px-2 text-text_pale">{refund.comments}</td>
            </tr>
          )
          : null,
      ])}
    </DetailTable>
  );
};

const OrderMain: FC<{ order: IOrder }> = ({ order }) => (
  <div className="flex flex-col gap-4">
    <DetailPanel title="Main Info">
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-x-8">
        <div>
          <DetailRow label="Order#" value={order.orderId} boxed />
          <DetailRow label="IP Address" value={order.ip} />
          <DetailRow label="Website" value={order.fromSite} />
          {/*
            A plain box for the status. The old page drove it from Department /
            Category / Subcategory selects that filled this in; those come later.
          */}
          <DetailRow label="Status" value={order.status} boxed />
          <DetailRow label="Created" value={stamp(order.timeCreate)} boxed />
          <DetailRow label="Updated" value={stamp(order.timeUpdate)} boxed />
          <DetailRow label="Scope" value={order.fromSiteCleaned} />
          <DetailRow label="Campaign" value={order.campaignId} />
          <DetailRow label="Referral" value={order.entryFrom} />
          <DetailRow label="Invoice" value={order.invoice} />
        </div>

        <div>
          <DetailRow label="Part Price" value={money(partPrice(order))} />
          <DetailRow
            label="Coupon Discount"
            value={money(order.discountAmount)
              ? `${money(order.discountAmount)}${order.couponCode ? ` (Coupon Code: ${order.couponCode})` : ''}`
              : null}
          />
          <DetailRow label="Freight Discount" value={money(freightDiscount(order))} />
          <DetailRow label="Subtotal" value={money(subtotal(order))} />
          <DetailRow label="Shipping" value={money(order.shipping)} boxed />
          <DetailRow label="Shipping Protection Fee" value={money(order.shippingProtectionFee)} boxed />
          <DetailRow label="Handling" value={money(order.handling)} boxed />
          <DetailRow label="Taxes" value={money(order.taxes)} boxed />
          <DetailRow label="Core" value={money(order.corePrice)} />
          <DetailRow
            label="Total"
            value={<span className="font-semibold">{money(order.totalPrice)}</span>}
            boxed
          />
          <DetailRow label="vPrice" value={money(vendorPrice(order))} />
          <DetailRow label="fPrice" value={money(feedPrice(order))} />
          <DetailRow label="Shipping To Billing Dist" value={order.billingShippingDistance} />
          <DetailRow label="Nearby" value={order.nearby} />
          <DetailRow label="Distance" value={order.distance} />
        </div>
      </div>

      <div className="mt-4">
        <div className="text-text_pale mb-1">Comments</div>
        <div className="rounded-md ring-1 ring-inset ring-gray-200 bg-white px-3 py-2 min-h-[80px] whitespace-pre-line">
          {order.notes || <span className="text-gray-300">—</span>}
        </div>
      </div>
    </DetailPanel>

    <DetailPanel title="Refund Info">
      <RefundTable order={order} vendor={false} />
    </DetailPanel>

    <DetailPanel title="Vendor Refund Info">
      <RefundTable order={order} vendor={true} />
    </DetailPanel>
  </div>
);

export default OrderMain;
